import type {
  CustomFieldTarget,
  EntityQueryTarget,
} from "@/Boards/definition/entityColumns";
import { customFieldDisplayName } from "@/shared/domain/customFields/customFieldPresentation";
import {
  TargetEntityType,
  type CustomFieldRecord,
} from "@/shared/domain/customFields/CustomFieldRecord";
import { roboqlFields } from "@/shared/domain/query/roboqlFields";

/** How a query spells a custom field wherever it names one: in a filter, a
 *  sort, a group key and `fields`. */
export const CUSTOM_FIELD_PREFIX = "custom.";

/** The custom-field entity behind each target whose record carries custom
 *  fields (`CustomFieldTarget`), and no other. */
export const CUSTOM_FIELD_ENTITY_BY_TARGET: Partial<
  Record<EntityQueryTarget, TargetEntityType>
> = {
  sessions: TargetEntityType.Session,
  events: TargetEntityType.Event,
  datasets: TargetEntityType.Dataset,
  devices: TargetEntityType.Device,
  collections: TargetEntityType.Collection,
} satisfies Record<CustomFieldTarget, TargetEntityType>;

/** The entity types the custom fields of `targets` are stored under
 *  (`CUSTOM_FIELD_ENTITY_BY_TARGET`): the lists of custom fields a reader of
 *  those catalogs needs. A catalog with no custom fields adds none. */
export function customFieldEntityTypesOf(
  targets: Iterable<EntityQueryTarget>,
): Set<TargetEntityType> {
  const entityTypes = new Set<TargetEntityType>();
  for (const target of targets) {
    const entityType = CUSTOM_FIELD_ENTITY_BY_TARGET[target];
    if (entityType !== undefined) {
      entityTypes.add(entityType);
    }
  }
  return entityTypes;
}

/** The display names of the catalog fields a grouped count can group by
 *  (`groupByFieldsForTarget`), keyed by field. A field without an entry is
 *  shown as written. */
const GROUP_BY_FIELD_NAMES: Readonly<Record<string, string>> = {
  association_id: "Dataset or collection ID",
  collection_id: "Collection ID",
  created: "Created",
  created_by: "Created by",
  device_id: "Device ID",
  device_type_id: "Device type ID",
  duration: "Duration (ns)",
  end_time: "End time",
  min_timestamp_ns: "Start time",
  modified: "Modified",
  modified_by: "Modified by",
  name: "Name",
  node_id: "File ID",
  relative_path: "Path",
  resource_count: "Resource count",
  resource_type: "Resource type",
  session_id: "Session ID",
  size: "Size (bytes)",
  start_time: "Start time",
  updated: "Updated",
  updated_by: "Updated by",
};

/**
 * The catalog fields a grouped count of `target` can group by, in catalog
 * order: the RoboQL catalog's sortable fields, under their canonical names
 * (`session_id`, not its alias `id`). Of a target's own fields, the server
 * counts the values of exactly these, under these names or their aliases
 * (`countable_field_keys` in the service's
 * `persistence/postgres/query/countable_fields.py`). It also counts an org's
 * Ready custom field as `custom.<name>` on every target with custom fields
 * (`CUSTOM_FIELD_ENTITY_BY_TARGET`).
 */
export function groupByFieldsForTarget(target: EntityQueryTarget): string[] {
  return (roboqlFields[target] ?? [])
    .filter((field) => field.is_sortable)
    .map((field) => field.label);
}

/**
 * What a field of an entity is called where an author or viewer reads it,
 * given `customFields`, the org's Ready custom fields of `target`:
 * - a custom field `custom.<name>`: the display name in the org's record
 *   of it; `<name>` when the record gives none, and while the records are
 *   not passed, have not arrived, or do not list the field;
 * - a catalog field a grouped count of `target` can group by
 *   (`groupByFieldsForTarget`): its name in `GROUP_BY_FIELD_NAMES`
 *   (`device_id` is "Device ID");
 * - any other field, such as a catalog field no grouped count groups by
 *   (`status`): the field as written.
 */
export function entityFieldDisplayName(
  target: EntityQueryTarget,
  field: string,
  customFields: readonly CustomFieldRecord[] | undefined,
): string {
  if (field.startsWith(CUSTOM_FIELD_PREFIX)) {
    const fieldName = field.slice(CUSTOM_FIELD_PREFIX.length);
    const record = customFields?.find(
      (candidate) => candidate.field_name === fieldName,
    );
    return record === undefined ? fieldName : customFieldDisplayName(record);
  }
  return groupByFieldsForTarget(target).includes(field)
    ? (GROUP_BY_FIELD_NAMES[field] ?? field)
    : field;
}
